import { useLayoutEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'

import { Alert } from '@vtex/shoreline'
import { t } from 'i18next'

import { AlertWithTitle } from '~/components/AlertWithTitle'
import { DatepickerHeader } from '~/components/datepicker-header'
import HeaderPages from '~/components/HeaderPages'
import { LogoStore } from '~/components/LogoStore'
import PageTitle from '~/components/PageTitle'
import Status from '~/components/Status'
import { Badge } from '~/components/UI/Badge'
import {
  formatDateToString,
  getRetailMediaCounterpart,
  history,
  isAdvertiser,
  isPublisher,
  isReadOnly
} from '~/helpers'
import { handleErrorMessage } from '~/helpers/errors'
import { useAppInfo } from '~/hooks/useAppInfo'
import useQueryParams from '~/hooks/useQuery'
import CampaignLogs from '~/modules/retailMedia/components/CampaignLogs'
import { ObjectiveBasedCampaignBadge } from '~/modules/retailMedia/components/ObjectiveBased'
import ToogleActiveCampaign from '~/modules/retailMedia/components/ToogleActiveCampaign'
import { getLabelAdType } from '~/modules/retailMedia/dtos/common/campaign/format'
import { campaignStatusBadge } from '~/modules/retailMedia/helpers/campaignStatusBadge'
import { getCampaign } from '~/modules/retailMedia/store/campaign/actions'
import { useAppSelector } from '~/store/hooks'

import DebugSettings from './components/Debug/Settings'
import DetailsViewCampaign from './components/Details'

const getLabelBreadcrumb = (
  isWhiteLabel: boolean,
  accountId?: string,
  accountName?: string
) => {
  const hasAccount =
    (!isWhiteLabel || isPublisher()) && accountId && accountName

  return hasAccount
    ? t('rm:backToCampaignsWithName', { name: accountName })
    : t('rm:backToCampaigns')
}

const CampaignHeaderSubtitle = ({
  accountName,
  statusData,
  active,
  isSubpublisherReview,
  adType,
  isObjectiveBasedCampaign
}: {
  accountName?: string
  statusData: StatusData<string>
  active?: boolean
  isSubpublisherReview?: boolean
  adType?: CampaignAdType
  isObjectiveBasedCampaign: boolean
}) => {
  if (!accountName) {
    return null
  }

  return (
    <div className="d-inline-flex">
      <Status status={statusData} />
      {(isPublisher() || isReadOnly()) && !isSubpublisherReview && (
        <div className="ps-2 ms-2 border-start border-tertiary">
          <Badge template={active ? 'success' : 'warning'}>
            {active ? t('rm:activeCampaign') : t('rm:disabledCampaign')}
          </Badge>
        </div>
      )}
      <div className="ps-2 ms-2 border-start border-tertiary">
        {t('rm:campaignType')}: {getLabelAdType(adType)}
      </div>
      {isObjectiveBasedCampaign && (
        <>
          <div className="ps-2 ms-2 border-start border-tertiary" />
          <div className="align-self-center">
            <ObjectiveBasedCampaignBadge />
          </div>
        </>
      )}
    </div>
  )
}

const CampaignView = () => {
  const { id } = useParams()

  const { isWhiteLabel } = useAppInfo()

  const query = useQueryParams()
  const callback_url = query.get('callback_url')

  const dispatch = useDispatch()
  const {
    campaign: {
      item: { data, loading, error }
    }
  } = useAppSelector(state => state.retailMedia)

  // check is persited data on redux is valid
  const isValidContent = useMemo(() => id === data?.id, [data?.id, id])

  // TODO: implement logic to check if campaign is paused.
  // Blocked on backend: https://vtex-dev.atlassian.net/browse/VA-1310
  const shouldShowPausedCampaignAlert = false

  const { startDate, endDate } = useAppSelector(state => state.datepickerHeader)

  const queryParams = useMemo(
    () => ({
      start_date: formatDateToString(startDate, 'YYYY-MM-DD'),
      end_date: formatDateToString(endDate, 'YYYY-MM-DD'),
      campaign_id: id
    }),
    [startDate, endDate, id]
  )

  useLayoutEffect(() => {
    dispatch(getCampaign({ ...queryParams, account_info: true }))
  }, [dispatch, queryParams])

  /**
   * Handle header pages props
   */

  const headerPageProps: HeaderPageProps = useMemo(() => {
    if (error) {
      return {
        title: `${t('rm:Campaign')} - ${t('rm:campaign.error.loading')}`,
        goBack: () => history.back(),
        labelBreadcrumb: t('rm:back')
      }
    }

    if (!isValidContent) {
      return {
        title: `${t('rm:Campaign')} - ${t('rm:Loading')}`,
        goBack: () => history.back(),
        labelBreadcrumb: t('rm:back')
      }
    }

    const {
      publisher,
      advertiser,
      title = null,
      id,
      active,
      target
    } = data || {}
    const accountData = isPublisher() ? advertiser : publisher
    const { name: accountName, id: accountId } = accountData || {}
    const counterpartKey = getRetailMediaCounterpart()
    const backgroundLogo = data?.[counterpartKey]?.accountTheme?.header_color
    const linkLogo = data?.[counterpartKey]?.logo
    const alt = data?.[counterpartKey]?.name

    const labelBreadcrumb = getLabelBreadcrumb(
      isWhiteLabel,
      accountId,
      accountName
    )

    const backToApp =
      callback_url || `/retail-media/campaigns?rmid=${accountId}`

    const backTo =
      isWhiteLabel && !isPublisher() ? '/retail-media/campaigns' : backToApp

    const statusData = campaignStatusBadge(data?.status)

    const goBack = accountId ? null : () => history.back()
    const isObjectiveBasedCampaign =
      data.raw.strategy_type === 'objective_based'

    return {
      titleSideRightComponent: (!isWhiteLabel || isPublisher()) && (
        <LogoStore
          size="medium"
          backgroundLogo={backgroundLogo}
          linkLogo={linkLogo}
          alt={alt}
        />
      ),
      title: loading ? t('rm:Loading') : title,
      subtitle: (
        <CampaignHeaderSubtitle
          accountName={accountName}
          statusData={statusData}
          active={active}
          isSubpublisherReview={data?.isSubpublisherReview}
          adType={data?.adType}
          isObjectiveBasedCampaign={isObjectiveBasedCampaign}
        />
      ),
      backTo,
      goBack,
      labelBreadcrumb,
      className: 'has-datepicker',
      sideRightComponent: (
        <>
          {!!id && isAdvertiser() && !isReadOnly() && (
            <ToogleActiveCampaign showLabel data={{ id, active, target }} />
          )}
          {!data?.isSubpublisherReview && <DatepickerHeader />}
        </>
      )
    }
  }, [isValidContent, data, isWhiteLabel, callback_url, loading, error])

  /**
   * Handle head title tag
   */
  const pageTitle = useMemo(() => {
    if (error) {
      return `${t('rm:Campaign')} - ${t('rm:campaign.error.loading')}`
    }

    if ((!isValidContent && !loading) || loading)
      return `${t('rm:Campaign')} - ${t('rm:Loading')}`

    if (isValidContent && !!data?.title)
      return `${t('rm:Campaign')} - ${data.title}`

    return t('rm:Campaign')
  }, [data, isValidContent, loading, error])

  return (
    <>
      <PageTitle title={pageTitle} />

      <HeaderPages {...headerPageProps} />

      {shouldShowPausedCampaignAlert && (
        <Alert variant="warning" style={{ margin: 'var(--sl-space-2) 0' }}>
          {t('rm:objectiveBased.campaignDetails.alert.pausedCampaign')}
        </Alert>
      )}
      {!!error && (
        <AlertWithTitle
          style={{ margin: '1rem 0' }}
          title={t('rm:somethingWentWrong.alert')}
          description={handleErrorMessage({
            error,
            fallbackMessage: t('rm:pages.campaign.view.fallback.message')
          })}
        />
      )}

      {!error && (
        <>
          <DebugSettings />

          {isValidContent && (
            <>
              <DetailsViewCampaign data={data} loading={loading} />

              {!data?.isSubpublisherReview && <CampaignLogs campaignId={id} />}
            </>
          )}
        </>
      )}
    </>
  )
}

export default CampaignView
